const getByPath = (source: unknown, path?: string): unknown => {
  if (!source || !path) return undefined;
  return path
    .replace(/\[(\d+)\]/g, ".$1")
    .split(".")
    .reduce(
      (accumulator: unknown, key) =>
        accumulator != null
          ? (accumulator as Record<string, unknown>)[key]
          : undefined,
      source,
    );
};

const normalizeUniqueValue = (value: unknown): string =>
  String(value ?? "").trim();

type UniqueFieldShape = {
  key?: string;
  label?: string;
  validation?: { uniqueAcrossItems?: boolean; uniqueMessage?: string };
};

export const isUniqueAcrossItemsField = (field: unknown): boolean =>
  (field as UniqueFieldShape | undefined)?.validation?.uniqueAcrossItems ===
  true;

export const getUniqueAcrossItemsMessage = (field: unknown): string => {
  const uniqueField = field as UniqueFieldShape | undefined;
  return (
    uniqueField?.validation?.uniqueMessage ||
    `${uniqueField?.label || uniqueField?.key || "This value"} has already been added`
  );
};

export const getDisabledUniqueAcrossItemsValues = (
  field: unknown,
  items: unknown[] = [],
  currentIndex = -1,
): Set<string> => {
  const values = new Set<string>();
  if (!isUniqueAcrossItemsField(field)) return values;
  const uniqueField = field as UniqueFieldShape;

  items.forEach((item, index) => {
    if (index === currentIndex) return;
    const value = normalizeUniqueValue(getByPath(item, uniqueField.key));
    if (value) values.add(value);
  });

  return values;
};

export const getUniqueAcrossItemsError = (
  field: unknown,
  value: unknown,
  items: unknown[] = [],
  currentIndex = -1,
): string | undefined => {
  const normalizedValue = normalizeUniqueValue(value);
  if (!normalizedValue) return undefined;

  return getDisabledUniqueAcrossItemsValues(field, items, currentIndex).has(
    normalizedValue,
  )
    ? getUniqueAcrossItemsMessage(field)
    : undefined;
};

export const getUniqueAcrossItemsContextFromPath = (
  formData: unknown,
  fullPath: string,
  fieldKey: string,
): { items: unknown[]; currentIndex: number } => {
  const suffix = `.${fieldKey}`;
  const itemPath = fullPath.endsWith(suffix)
    ? fullPath.slice(0, -suffix.length)
    : fullPath;
  const match = /^(.*)\[(\d+)\]$/.exec(itemPath);
  if (!match) return { items: [], currentIndex: -1 };

  const items = getByPath(formData, match[1]);
  return {
    items: Array.isArray(items) ? items : [],
    currentIndex: Number(match[2]),
  };
};
